<template>
  <!-- Cookie Banner as Footer -->
  <div
    v-if="showBanner"
    class="cookie-footer-banner"
  >
    <div class="cookie-banner-content">
      <div v-if="!showSettings" class="banner-simple">
        <div class="banner-message">
          <span class="cookie-icon">🍪</span>
          <span class="message-text">
            We value your privacy. We use cookies to enhance your experience.
            <router-link to="/cookie-policy" class="learn-more">Learn more</router-link>
          </span>
        </div>
        <div class="banner-actions">
          <button @click="acceptAll" class="btn btn-accept">Accept All</button>
          <button @click="rejectOptional" class="btn btn-reject">Reject Optional</button>
          <button @click="toggleSettings" class="btn btn-customize">Customize</button>
        </div>
      </div>

      <div v-else class="banner-settings">
        <!-- Settings Header -->
        <div class="settings-header">
          <h3>Cookie Preferences</h3>
          <button @click="toggleSettings" class="close-btn">×</button>
        </div>

        <!-- Settings Content -->
        <div class="settings-scroll">
          <!-- Necessary -->
          <div class="cookie-category necessary">
            <div class="category-header">
              <strong>Strictly Necessary</strong>
              <span class="always-active">Always Active</span>
            </div>
            <p class="category-desc">Essential cookies required for the platform to function.</p>
          </div>

          <!-- Functional -->
          <div class="cookie-category">
            <div class="category-header">
              <strong>Functional Cookies</strong>
              <label class="toggle">
                <input type="checkbox" v-model="preferences.functional">
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">Enhanced features and personalized settings.</p>
          </div>

          <!-- Analytics -->
          <div class="cookie-category">
            <div class="category-header">
              <strong>Analytics Cookies</strong>
              <label class="toggle">
                <input type="checkbox" v-model="preferences.analytics">
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">Help us understand how you use our platform.</p>
          </div>

          <!-- Marketing -->
          <div class="cookie-category">
            <div class="category-header">
              <strong>Marketing Cookies</strong>
              <label class="toggle">
                <input type="checkbox" v-model="preferences.marketing">
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="category-desc">Deliver relevant advertisements and measure campaigns.</p>
          </div>
        </div>

        <!-- Settings Actions -->
        <div class="settings-actions">
          <button @click="savePreferences" class="btn btn-save">Save Preferences</button>
          <button @click="acceptAll" class="btn btn-accept-all">Accept All</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { cookieService, type CookiePreferences } from '@/services/cookieService'

const showBanner = ref(false)
const showSettings = ref(false)
const preferences = ref<CookiePreferences>({
  necessary: true, // Always true
  functional: false,
  analytics: false,
  marketing: false
})

onMounted(() => {
  // Check if user has already made a choice
  if (!cookieService.hasConsented()) {
    // Show banner after a short delay
    setTimeout(() => {
      showBanner.value = true
    }, 1000)
  } else {
    // Load saved preferences and apply them
    preferences.value = cookieService.loadPreferences()
  }

  // Listen for show settings event (from cookie policy page)
  window.addEventListener('show-cookie-settings', () => {
    showBanner.value = true
    showSettings.value = true
  })
})

const acceptAll = () => {
  preferences.value = {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true
  }
  saveAndClose()
}

const rejectOptional = () => {
  preferences.value = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  }
  saveAndClose()
}

const savePreferences = () => {
  saveAndClose()
}

const saveAndClose = () => {
  // Save consent and preferences using the cookie service
  cookieService.savePreferences(preferences.value)
  
  // Close banner
  showBanner.value = false
  showSettings.value = false
}

const toggleSettings = () => {
  showSettings.value = !showSettings.value
}
</script>

<style scoped>
.cookie-footer-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #ffffff;
  border-top: 2px solid #3B82F6;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.cookie-banner-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 1.5rem;
}

/* Simple Banner View */
.banner-simple {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.banner-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 300px;
}

.cookie-icon {
  font-size: 2rem;
}

.message-text {
  font-size: 0.9rem;
  color: #4B5563;
  line-height: 1.5;
}

.learn-more {
  color: #3B82F6;
  text-decoration: none;
  font-weight: 500;
}

.learn-more:hover {
  text-decoration: underline;
}

.banner-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-accept {
  background-color: #3B82F6;
  color: white;
}

.btn-accept:hover {
  background-color: #2563EB;
}

.btn-reject {
  background-color: #E5E7EB;
  color: #374151;
}

.btn-reject:hover {
  background-color: #D1D5DB;
}

.btn-customize {
  background-color: transparent;
  color: #374151;
  border: 1px solid #D1D5DB;
}

.btn-customize:hover {
  background-color: #F9FAFB;
}

/* Settings View */
.banner-settings {
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #E5E7EB;
  margin-bottom: 0.75rem;
}

.settings-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9CA3AF;
  cursor: pointer;
  padding: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #4B5563;
}

.settings-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-bottom: 0.75rem;
}

.cookie-category {
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
}

.cookie-category.necessary {
  background-color: #FEF3C7;
  border-color: #FCD34D;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.category-header strong {
  font-size: 0.9rem;
  color: #111827;
}

.always-active {
  background-color: #FCD34D;
  color: #78350F;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.category-desc {
  font-size: 0.8rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.4;
}

/* Toggle Switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #D1D5DB;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle input:checked + .toggle-slider {
  background-color: #3B82F6;
}

.toggle input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.settings-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #E5E7EB;
}

.btn-save {
  flex: 1;
  background-color: #3B82F6;
  color: white;
}

.btn-save:hover {
  background-color: #2563EB;
}

.btn-accept-all {
  flex: 1;
  background-color: #E5E7EB;
  color: #374151;
}

.btn-accept-all:hover {
  background-color: #D1D5DB;
}

/* Responsive */
@media (max-width: 768px) {
  .banner-simple {
    flex-direction: column;
    align-items: stretch;
  }

  .banner-actions {
    width: 100%;
  }

  .banner-actions .btn {
    flex: 1;
  }

  .settings-actions {
    flex-direction: column;
  }
}
</style>
